import React from "react";
import {
  ShieldCheck,
  Lock,
  KeyRound,
  HardDrive,
  Layers,
  Fingerprint,
  KeySquare,
  UserCog,
  Globe,
  Radar,
  RefreshCw,
  ScanSearch,
  Boxes,
  Award,
} from "lucide-react";
import { SECURITY_PAGE } from "../data/mock";
import { FadeIn, SplitText, BlurText, SpotlightCard, HexPattern, Eyebrow, FloatingHexes } from "../components/reactbits/Anim";

const ICONS = { Lock, KeyRound, HardDrive, Layers, Fingerprint, KeySquare, UserCog, Globe, Radar, RefreshCw, ScanSearch, Boxes };

const cardCls =
  "rounded-2xl border border-[#EDE5D3] bg-white shadow-[0_2px_16px_rgba(31,27,19,0.04)] transition-all duration-300 hover:border-[#F0A322]/50 hover:shadow-[0_8px_30px_rgba(232,147,12,0.12)]";

const CheckItem = ({ text }) => (
  <li className="flex items-start gap-3 rounded-xl border border-[#EDE5D3] bg-white px-5 py-3.5 shadow-[0_2px_8px_rgba(31,27,19,0.03)] transition-colors duration-300 hover:border-[#F0A322]/50">
    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#E8930C]" />
    <span className="text-sm text-[#4A4436]">{text}</span>
  </li>
);

const Security = () => (
  <div className="bg-white text-[#1F1B13]" data-testid="security-page">
    {/* ===== Page hero ===== */}
    <section className="relative overflow-hidden bg-[#FDFBF7] pt-40 pb-24">
      <HexPattern opacity={0.07} />
      <FloatingHexes />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.13), transparent 60%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Eyebrow>security</Eyebrow>
        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-bold uppercase leading-tight sm:text-6xl" data-testid="security-page-heading">
          <SplitText text="MEDHIVE'S COMMITMENT" />{" "}
          <span className="text-[#E8930C]">
            <SplitText text="TO SECURITY" delay={0.3} />
          </span>
        </h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#5D564A] sm:text-lg">{SECURITY_PAGE.body}</p>
      </div>
    </section>

    {/* ===== Security groups ===== */}
    {SECURITY_PAGE.groups.map((group, idx) => (
      <section key={group.title} className={`relative overflow-hidden py-20 lg:py-24 ${idx % 2 ? "bg-[#FAF6EE]" : "bg-white"}`}>
        {idx % 2 === 1 && <HexPattern opacity={0.04} />}
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <FadeIn>
            <Eyebrow>{group.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold uppercase text-[#1F1B13] sm:text-4xl">
              <BlurText text={group.title} />
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {group.items.map((item, i) => {
              const Icon = ICONS[item.icon];
              return (
                <FadeIn key={item.title} delay={i * 0.08} className="h-full">
                  <SpotlightCard className={`h-full ${cardCls} p-7`}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0A322]/[0.12] text-[#B45309]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-heading text-base font-semibold text-[#1F1B13]">{item.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#6B6353]">{item.text}</p>
                  </SpotlightCard>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
    ))}

    {/* ===== Compliance checklist ===== */}
    <section className="relative overflow-hidden bg-[#FDFBF7] py-20 lg:py-24">
      <HexPattern opacity={0.04} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:items-start">
          <FadeIn>
            <Eyebrow>solutions</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-4xl">
              <BlurText text={SECURITY_PAGE.compliance.title} />
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#5D564A]">{SECURITY_PAGE.compliance.body}</p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {SECURITY_PAGE.compliance.items.map((item) => (
                <CheckItem key={item} text={item} />
              ))}
            </ul>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* ===== Data Governance ===== */}
    <section className="py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Eyebrow>data governance</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-4xl">
            <BlurText text={SECURITY_PAGE.governance.title} />
          </h2>
        </FadeIn>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {SECURITY_PAGE.governance.sections.map((sec, i) => (
            <FadeIn key={sec.title} delay={i * 0.1} className="h-full">
              <SpotlightCard className={`h-full ${cardCls} p-8`}>
                <h3 className="font-heading text-xl font-semibold text-[#1F1B13]">{sec.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#6B6353]">{sec.body}</p>
                <ul className="mt-6 space-y-3">
                  {sec.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#4A4436]">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#E8930C]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* ===== Cloud environment ===== */}
    <section className="relative overflow-hidden bg-[#FAF6EE] py-20 lg:py-24">
      <HexPattern opacity={0.04} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:items-start">
          <FadeIn>
            <Eyebrow>infrastructure</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold text-[#1F1B13] sm:text-4xl">
              <BlurText text={SECURITY_PAGE.cloud.title} />
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#5D564A]">{SECURITY_PAGE.cloud.body}</p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {SECURITY_PAGE.cloud.items.map((item) => (
                <CheckItem key={item} text={item} />
              ))}
            </ul>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* ===== Compliance Roadmap ===== */}
    <section className="py-24 lg:py-28" data-testid="compliance-roadmap">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <Eyebrow>{SECURITY_PAGE.roadmapEyebrow}</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold uppercase text-[#1F1B13] sm:text-4xl">
            <BlurText text="COMPLIANCE" /> <span className="text-[#E8930C]"><BlurText text="ROADMAP" delay={0.2} /></span>
          </h2>
          <p className="mt-5 text-sm text-[#8A8272]">
            Nothing on this page is presented as a completed certification until it is certified.
          </p>
        </FadeIn>
        <div className="mx-auto mt-14 grid max-w-4xl gap-5 sm:grid-cols-3">
          {SECURITY_PAGE.roadmap.map((item, i) => (
            <FadeIn key={item.title} delay={i * 0.08} className="h-full">
              <SpotlightCard className={`flex h-full min-h-44 flex-col items-center justify-center ${cardCls} p-6 text-center`}>
                <Award className="h-9 w-9 text-[#E8930C]" />
                <h3 className="mt-3 font-heading text-base font-semibold text-[#1F1B13]">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-[#8A8272]">{item.note}</p>
              </SpotlightCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Security;
